import React, { useState } from 'react';
import { 
  Utensils, 
  Activity, 
  Search, 
  Brain, 
  Plus, 
  TrendingUp, 
  Smartphone,
  Clock,
  Target,
  AlertTriangle,
  CheckCircle,
  X
} from 'lucide-react';
import { useExampleData } from '../../data/example/ExampleDataProvider';
import { MockBadge } from '../common/MockBadge';
import { DataDisclaimer } from '../common/DataDisclaimer';

interface FoodEntry {
  id: string;
  name: string;
  category: 'carbohydrate' | 'protein' | 'fat' | 'vegetable' | 'fruit' | 'dairy' | 'grain' | 'snack' | 'beverage';
  amount: string;
  unit: string;
  timestamp: string;
  glucoseImpact?: number; // predicted or measured impact
  carbCount?: number;
  glycemicIndex?: number;
  notes?: string;
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  ingredients?: string[];
  usdaFdcId?: number; // Link to USDA food database
  personalGlycemicIndex?: number; // User's personal response
}

interface FoodCorrelation {
  foodItem: string;
  symptom: string;
  correlation: number;
  timeDelay: string; // e.g., "30 minutes", "2 hours"
  confidence: number;
  occurrences: number;
  lastOccurrence: string;
  glucoseSpike?: number;
  duration?: number;
}

interface MealPlan {
  id: string;
  name: string;
  targetCarbs: number;
  foods: FoodEntry[];
  estimatedGlucoseImpact: number;
}

export const FoodTracking: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'food-log' | 'glucose-tracker' | 'food-database' | 'correlations'>('food-log');
  const [isLoggerOpen, setIsLoggerOpen] = useState(false);
  
  const [foodEntries, setFoodEntries] = useState<FoodEntry[]>(loadDemoFoodEntries());

  const { getFoodCorrelations, shouldShowExampleData } = useExampleData();
  const isExampleData = shouldShowExampleData('food-tracking');
  const correlations = isExampleData ? getFoodCorrelations() : [];

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'carbohydrate': 'bg-orange-100 text-orange-800',
      'protein': 'bg-red-100 text-red-800',
      'fat': 'bg-yellow-100 text-yellow-800',
      'vegetable': 'bg-green-100 text-green-800',
      'fruit': 'bg-purple-100 text-purple-800',
      'dairy': 'bg-blue-100 text-blue-800',
      'grain': 'bg-amber-100 text-amber-800',
      'snack': 'bg-pink-100 text-pink-800',
      'beverage': 'bg-cyan-100 text-cyan-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const getGlucoseImpactColor = (impact: number) => {
    if (impact <= 20) return 'text-green-600';
    if (impact <= 40) return 'text-yellow-600';
    if (impact <= 60) return 'text-orange-600';
    return 'text-red-600';
  };

  const getCorrelationColor = (correlation: number) => {
    if (correlation >= 0.7) return 'text-red-600 bg-red-50 border-red-200';
    if (correlation >= 0.5) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-yellow-600 bg-yellow-50 border-yellow-200';
  };

  const todaysCarbs = foodEntries
    .filter(entry => new Date(entry.timestamp).toDateString() === new Date().toDateString())
    .reduce((sum, entry) => sum + (entry.carbCount || 0), 0);

  const avgGlucoseImpact = foodEntries
    .filter(entry => entry.glucoseImpact)
    .reduce((sum, entry) => sum + (entry.glucoseImpact || 0), 0) / 
    foodEntries.filter(entry => entry.glucoseImpact).length;
    
  const handleSaveMeal = (mealData: MealData) => {
    // In a real implementation, this would save to the database
    // For now, we'll just close the modal
    setIsLoggerOpen(false);
    
    // Show a success message or update the UI as needed
    console.log('Meal saved:', mealData);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-800">Diabetes Food & Glucose Tracking {isExampleData && <MockBadge variant="small" className="ml-2" />}</h2>
          <p className="text-slate-600 mt-1">Advanced food-glucose correlation analysis with USDA database integration</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
            <Brain className="h-4 w-4" />
            <span>AI Food Analysis</span>
          </button>
          <button 
            onClick={() => setIsLoggerOpen(true)}
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all transform hover:scale-105"
          >
            <Plus className="h-4 w-4" />
            <span>Quick Log Meal</span>
          </button>
        </div>
      </div>

      {isExampleData && (
        <DataDisclaimer 
          type="info"
          title="Example Food Tracking Data"
          message="This shows example food correlations and tracking data for demonstration. Real data will appear when you log your meals and connect your glucose monitor."
          className="mb-4"
        />
      )}

      {/* Navigation Tabs */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex space-x-4">
          {[
            { id: 'food-log', label: 'Food Log', icon: Utensils },
            { id: 'glucose-tracker', label: 'Glucose Tracker', icon: Activity },
            { id: 'food-database', label: 'USDA Food Database', icon: Search },
            { id: 'correlations', label: 'Food Correlations', icon: Brain }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon className={`h-5 w-5 ${activeTab === tab.id ? 'text-blue-500' : 'text-slate-400'}`} />
                <span className="font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Diabetes-Focused Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Today's Carbs</p>
              <p className="text-2xl font-bold text-slate-800">{todaysCarbs}g</p>
            </div>
            <Utensils className="h-8 w-8 text-orange-500" />
          </div>
          <p className="text-xs text-slate-500 mt-1">Target: 150-200g</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Avg Glucose Impact</p>
              <p className={`text-2xl font-bold ${getGlucoseImpactColor(avgGlucoseImpact)}`}>
                {avgGlucoseImpact.toFixed(0)}
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-blue-500" />
          </div>
          <p className="text-xs text-slate-500 mt-1">mg/dL estimated</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Food Correlations</p>
              <p className="text-2xl font-bold text-slate-800">{correlations.length}</p>
            </div>
            <Brain className="h-8 w-8 text-purple-500" />
          </div>
          <p className="text-xs text-slate-500 mt-1">AI-detected patterns</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Freestyle Libre 3</p>
              <p className="text-2xl font-bold text-green-600">●</p>
            </div>
            <Smartphone className="h-8 w-8 text-green-500" />
          </div>
          <p className="text-xs text-slate-500 mt-1">Connected & syncing</p>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'food-log' && (
        <div className="space-y-6">
          {/* Recent Food Entries */}
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-slate-800">Recent Food Entries</h3>
                <button 
                  onClick={() => setIsLoggerOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Entry</span>
                </button>
              </div>
            </div>
            <div className="p-6">
              {foodEntries.length > 0 ? (
                <div className="space-y-4">
                  {foodEntries.slice(0, 5).map((entry) => (
                    <div key={entry.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(entry.category)}`}>
                          {entry.category}
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-800">{entry.name}</h4>
                          <p className="text-sm text-slate-600">
                            {entry.amount} {entry.unit} • {entry.mealType} • {new Date(entry.timestamp).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        {entry.carbCount && (
                          <p className="text-sm font-medium text-slate-800">{entry.carbCount}g carbs</p>
                        )}
                        {entry.glucoseImpact && (
                          <p className={`text-sm ${getGlucoseImpactColor(entry.glucoseImpact)}`}>
                            +{entry.glucoseImpact} mg/dL
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Utensils className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-600 mb-2">No food entries yet</h3>
                  <p className="text-slate-500 mb-4">Start logging your meals to track your nutrition and glucose impact</p>
                  <button 
                    onClick={() => setIsLoggerOpen(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 mx-auto"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Log Your First Meal</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'correlations' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold text-slate-800">AI-Detected Food Correlations</h3>
                <p className="text-slate-600">Patterns discovered from your food and glucose data</p>
              </div>
              <div className="flex items-center space-x-2">
                <Brain className="h-5 w-5 text-purple-500" />
                <span className="text-sm text-slate-600">AI Analysis</span>
              </div>
            </div>
            
            {correlations.length > 0 ? (
              <div className="space-y-4">
                {correlations.map((correlation, index) => (
                  <div key={index} className={`p-4 rounded-lg border ${getCorrelationColor(correlation.correlation)}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h4 className="font-semibold text-slate-800">{correlation.foodItem}</h4>
                          <span className="text-sm text-slate-600">→</span>
                          <span className="font-medium text-slate-800">{correlation.symptom}</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-slate-600">Correlation</p>
                            <p className="font-semibold">{(correlation.correlation * 100).toFixed(0)}%</p>
                          </div>
                          <div>
                            <p className="text-slate-600">Time Delay</p>
                            <p className="font-semibold">{correlation.timeDelay}</p>
                          </div>
                          <div>
                            <p className="text-slate-600">Confidence</p>
                            <p className="font-semibold">{correlation.confidence}%</p>
                          </div>
                          <div>
                            <p className="text-slate-600">Occurrences</p>
                            <p className="font-semibold">{correlation.occurrences}</p>
                          </div>
                        </div>
                        {correlation.glucoseSpike && (
                          <div className="mt-3 p-3 bg-red-50 rounded border border-red-200">
                            <p className="text-sm text-red-800">
                              <strong>Glucose Impact:</strong> +{correlation.glucoseSpike} mg/dL over {correlation.duration} minutes
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button className="p-2 text-slate-400 hover:text-slate-600 transition-colors">
                          <Target className="h-4 w-4" />
                        </button>
                        <button className="p-2 text-slate-400 hover:text-slate-600 transition-colors">
                          <AlertTriangle className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Brain className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-600 mb-2">No correlations detected yet</h3>
                <p className="text-slate-500">Start logging your meals and glucose readings to discover patterns</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Other tab content would go here */}
    </div>
  );
};

// Helper function to load demo food entries
function loadDemoFoodEntries(): FoodEntry[] {
  return [
    {
      id: '1',
      name: 'Steel Cut Oatmeal',
      category: 'grain',
      amount: '1',
      unit: 'cup',
      timestamp: '2024-01-15T08:00:00Z',
      glucoseImpact: 28,
      carbCount: 27,
      glycemicIndex: 55,
      mealType: 'breakfast',
      notes: 'With berries and nuts'
    },
    {
      id: '2',
      name: 'Grilled Chicken Breast',
      category: 'protein',
      amount: '6',
      unit: 'oz',
      timestamp: '2024-01-15T12:30:00Z',
      glucoseImpact: 15,
      carbCount: 0,
      mealType: 'lunch',
      notes: 'With steamed vegetables'
    }
  ];
}
